import {gql} from '@apollo/client';
import {Select} from '@mantine/core';
import React, {useMemo} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {PLATE_DESIGN_TEMPLATE_TABLE_NAME} from 'notebook/template-collections/TemplateCollectionTypes';
import getTestProps from 'test/lib/cypress/getTestProps';

import {
  ListPlateDesignTemplatesQuery,
  ListPlateDesignTemplatesQueryVariables,
} from './__generated/ListPlateDesignTemplatesQuery';

export const LIST_PLATE_DESIGN_TEMPLATES_QUERY = gql`
  query ListPlateDesignTemplatesQuery(
    $searchInput: StrictString
    $typeFilter: [StrictString!]
    $archiveFilter: [StrictString]
    $limitOffsetInput: LimitOffsetInput
  ) @skipAudit @engComponent(name: NOTEBOOK) {
    collectiblesListing(
      searchInput: $searchInput
      typeFilter: $typeFilter
      archiveFilter: $archiveFilter
      limitOffsetInput: $limitOffsetInput
    ) {
      total
      collectibles {
        ... on TemplateCollectibleInterface {
          id
          name
        }
        ... on PlateDesignTemplateNode {
          id
          name
          scratchPlate {
            ... on ScratchPlateNode {
              id
            }
          }
        }
      }
    }
  }
`;

interface PlateDesignTemplateSelectorProps {
  selectedTemplateId: string | null;
  onSelectTemplate: (templateId: string, scratchPlateId: string) => void;
  onClearTemplate?: () => void;
  disabled?: boolean;
}

const CLEAR_TEMPLATE_VALUE = '__clear_plate_design_template__';
const CLEAR_TEMPLATE_GROUP = 'Clear plate design template';
const PLATE_DESIGN_TEMPLATE_GROUP = 'Plate design templates';

export default function PlateDesignTemplateSelector({
  selectedTemplateId,
  onSelectTemplate,
  onClearTemplate,
  disabled,
}: PlateDesignTemplateSelectorProps): JSX.Element {
  const result = useSkippableBenchlingQuery<
    ListPlateDesignTemplatesQuery,
    ListPlateDesignTemplatesQueryVariables
  >(LIST_PLATE_DESIGN_TEMPLATES_QUERY, {
    variables: {
      searchInput: null,
      typeFilter: [PLATE_DESIGN_TEMPLATE_TABLE_NAME],
      archiveFilter: [null],
      limitOffsetInput: {limit: 50, offset: 0},
    },
  });

  const {templates, scratchPlateIdByTemplateId} = useMemo(() => {
    if (result.type !== QueryResultType.SUCCESS) {
      return {templates: [], scratchPlateIdByTemplateId: new Map<string, string>()};
    }
    const accessibleTemplates = result.data.collectiblesListing.collectibles.flatMap((c) => {
      if (c.__typename !== 'PlateDesignTemplateNode' || isInaccessible(c.scratchPlate)) {
        return [];
      }
      return [{template: c, scratchPlateId: c.scratchPlate.id}];
    });
    return {
      templates: accessibleTemplates.map(({template}) => template),
      scratchPlateIdByTemplateId: new Map(
        accessibleTemplates.map(({template, scratchPlateId}) => [template.id, scratchPlateId])
      ),
    };
  }, [result]);

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) ?? null;
  const options = [
    ...(onClearTemplate == null
      ? []
      : [
          {
            group: CLEAR_TEMPLATE_GROUP,
            label: 'No plate design template',
            value: CLEAR_TEMPLATE_VALUE,
          },
        ]),
    ...templates.map((template) => ({
      group: onClearTemplate != null ? PLATE_DESIGN_TEMPLATE_GROUP : undefined,
      label: template.name,
      value: template.id,
    })),
  ];

  function handleChange(value: string | null): void {
    if (value === CLEAR_TEMPLATE_VALUE) {
      onClearTemplate?.();
      return;
    }
    if (value == null) {
      return;
    }
    const scratchPlateId = scratchPlateIdByTemplateId.get(value);
    if (scratchPlateId != null) {
      onSelectTemplate(value, scratchPlateId);
    }
  }

  return (
    <Select
      aria-label="Plate design template"
      data={options}
      disabled={disabled}
      nothingFound="No plate design templates found"
      onChange={handleChange}
      placeholder="Select template..."
      searchable={true}
      size="md"
      styles={(theme) => ({
        separator: {
          '&:first-of-type': {display: 'none'},
          borderTop: `1px solid ${theme.colors.gray[3]}`,
          margin: 0,
          padding: 0,
        },
        separatorLabel: {display: 'none'},
      })}
      value={selectedTemplate?.id ?? null}
      wrapperProps={getTestProps({component: 'PlateDesignTemplateSelector', element: 'root'})}
    />
  );
}
